import React from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { withStyles } from '@mui/styles'

import { Typography } from '@mui/material'

import { getISOStringInitDate } from '../../core/utils/dateUtils'
import { dateInputStyles } from './date-input.styles'

/**
 * @typedef {object} DateChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {string} target.value - La fecha seleccionada en formato ISO string (ej. '2024-01-15T00:00:00.000Z').
 */

/**
 * @typedef {object} DateInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {string} [value] - La fecha actualmente seleccionada en formato ISO string o Date.
 * @property {function(DateChangeEvent): void} onChange - La función callback que se ejecuta cuando la fecha cambia.
 * @property {boolean} [readOnly=false] - Si es `true`, muestra la fecha como texto de solo lectura en lugar de un selector interactivo.
 * @property {Date} [minDate] - La fecha mínima seleccionable.
 * @property {Date} [maxDate] - La fecha máxima seleccionable.
 * @property {boolean} [allowKeyboard=true] - Si es `true`, permite la entrada de fecha mediante teclado.
 * @property {function} formatMessage - Función de react-intl para formatear mensajes internacionalizados.
 * @property {function} formatDate - Función de react-intl para formatear fechas según la localización del usuario.
 * @property {string} [title] - Título opcional que se muestra encima del selector de fecha.
 * @property {'small' | 'medium'} [fontSize] - Tamaño de la fuente para el título y el texto de solo lectura.
 */

/**
 * Un componente de selector de fecha con soporte para modo de solo lectura y validación de rangos.
 * 
 * Utiliza Material-UI DatePicker con adaptador date-fns para proporcionar una experiencia de selección
 * de fecha localizada. El componente puede funcionar en dos modos:
 * - **Modo interactivo**: Muestra un selector de fecha completo con calendario
 * - **Modo solo lectura**: Muestra la fecha formateada como texto estático
 * 
 * Las fechas se manejan internamente en formato ISO string para garantizar la compatibilidad con APIs
 * y bases de datos. El componente aplica automáticamente la localización del usuario para el formato
 * de visualización.
 * 
 * @component
 * @example
 * const MyForm = () => {
 *   const { formatMessage, formatDate } = useIntl();
 *   const [formData, setFormData] = useState({ fecha_inicio: null });
 * 
 *   const handleChange = (e) => {
 *     setFormData({ ...formData, [e.target.name]: e.target.value });
 *   };
 * 
 *   return (
 *     <DateInput
 *       name="fecha_inicio"
 *       title="Fecha de Inicio"
 *       value={formData.fecha_inicio}
 *       onChange={handleChange}
 *       minDate={new Date()}
 *       formatMessage={formatMessage}
 *       formatDate={formatDate}
 *     />
 *   );
 * };
 * 
 * @param {DateInputProps} props - Las props del componente DateInput.
 */
export const DateInput = React.memo(
  withStyles(dateInputStyles)(
    ({
      classes,
      readOnly = false,
      minDate,
      maxDate,
      allowKeyboard = true,
      formatMessage,
      formatDate,
      ...props
    }) => {
      const typographyVariant = props.fontSize === 'small' ? '2' : '1'

      const handleChange = (date, value) => {
        if (date && !isNaN(date.getTime())) {
          props.onChange({ target: { name: props.name, value: getISOStringInitDate(date) } })
        }
      }

      // const refuseDatePattern =
      //   '^(?:(?:31(\\/|-|\\.)(?:0?[13578]|1[02]))\\1|(?:(?:29|30)(\\/|-|\\.)(?:0?[13-9]|1[0-2])\\2))(?:(?:1[6-9]|[2-9]\\d)?\\d{2})$|^(?:29(\\/|-|\\.)0?2\\3(?:(?:(?:1[6-9]|[2-9]\\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\\d|2[0-8])(\\/|-|\\.)(?:(?:0?[1-9])|(?:1[0-2]))\\4(?:(?:1[6-9]|[2-9]\\d)?\\d{2})$'

      return (
        <div className={classes.root}>
          {props.title ? (
            <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
              {props.title}
            </Typography>
          ) : null}
          {readOnly ? (
            <Typography
              variant={`body${typographyVariant}`}
              color="textSecondary"
              style={{ whiteSpace: 'pre-line' }}
            >
              {props.value ? formatDate(props.value) : '--'}
            </Typography>
          ) : (
            <>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  {...props}
                  id={props.name}
                  name={props.name}
                  value={props.value || null}
                  onChange={handleChange}
                  minDate={minDate}
                  maxDate={maxDate}
                  inputFormat="dd/MM/yyyy"
                  inputVariant="outlined"
                  InputProps={{ className: classes.input }}
                  okLabel={formatMessage({ id: 'picker.ok' })}
                  cancelLabel={formatMessage({ id: 'picker.cancel' })}
                />
              </LocalizationProvider>
            </>
          )}
        </div>
      )
    }
  )
)
